import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../auth';
import MobileLogin from './MobileLogin';
import MobileLayout from './MobileLayout';
import StatoPage from './StatoPage';
import CercaPage from './CercaPage';
import ListePage from './ListePage';
import SsmatePage from './SsmatePage';
import CoperturaSsmatePage from './CoperturaSsmatePage';
import A456Page from './A456Page';
import CoperturaPage from './CoperturaPage';
import VfPage from './VfPage';
import AnalisiPage from './AnalisiPage';
import UtilizzoPage from './UtilizzoPage';
import RegioniPage from './RegioniPage';
import SalutePage from './SalutePage';
import { MobileTheme } from './mobileTheme';
import { SingleNotifProvider } from '../components/SingleNotif';
import { VerificaProvider } from './VerificaContext';
import InstallHint from './InstallHint';
import './mobile.css';

const TKEY = 'm_theme';
const MKEY = 'm_mode';

export default function MobileApp() {
  const { user, can } = useAuth();
  const appRef = useRef(null);
  const [theme, setThemeS] = useState(() => localStorage.getItem(TKEY) || 'blue');
  const [mode, setModeS] = useState(() => localStorage.getItem(MKEY) || 'dark');
  const setTheme = (t) => { setThemeS(t); try { localStorage.setItem(TKEY, t); } catch {} };
  const setMode = (m) => { setModeS(m); try { localStorage.setItem(MKEY, m); } catch {} };

  const [installOpen, setInstallOpen] = useState(false);
  const deferredPrompt = useRef(null);

  useEffect(() => {
    const h = (e) => { e.preventDefault(); deferredPrompt.current = e; };
    window.addEventListener('beforeinstallprompt', h);
    return () => window.removeEventListener('beforeinstallprompt', h);
  }, []);

  // auto-apertura a OGNI visita finché non installata (anche sul login, es. arrivo da QR).
  // Si ferma solo se l'app è installata (standalone) o se l'utente sceglie "Non mostrare più".
  useEffect(() => {
    const standalone = window.matchMedia && window.matchMedia('(display-mode: standalone), (display-mode: fullscreen)').matches;
    if (standalone) return;
    try {
      if (localStorage.getItem('m_install_dismissed') === '1') return;
    } catch (e) { return; }
    const t = setTimeout(() => setInstallOpen(true), 1200);
    return () => clearTimeout(t);
  }, []);

  // schermo intero: se aperto in-browser (non già installata come app), al PRIMO
  // tocco prova a entrare a tutto schermo (Fullscreen API richiede un gesto utente).
  // Su PWA installata (display standalone/fullscreen) non serve. iOS: non supportato
  // in-browser → usare "Aggiungi a Home".
  useEffect(() => {
    const standalone = window.matchMedia && window.matchMedia('(display-mode: standalone), (display-mode: fullscreen)').matches;
    const el = document.documentElement;
    if (standalone || !el.requestFullscreen) return;
    const go = () => { el.requestFullscreen().catch(() => {}); };
    document.addEventListener('pointerdown', go, { once: true });
    return () => document.removeEventListener('pointerdown', go);
  }, []);

  // niente pinch-zoom / doppio-tap zoom: comportamento da app nativa (solo sul mobile /m)
  useEffect(() => {
    const vp = document.querySelector('meta[name="viewport"]');
    const prev = vp ? vp.getAttribute('content') : null;
    if (vp) vp.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no, viewport-fit=cover');
    const noGesture = (e) => e.preventDefault();
    document.addEventListener('gesturestart', noGesture, { passive: false });
    document.addEventListener('gesturechange', noGesture, { passive: false });
    document.addEventListener('gestureend', noGesture, { passive: false });
    return () => {
      if (vp && prev) vp.setAttribute('content', prev);
      document.removeEventListener('gesturestart', noGesture);
      document.removeEventListener('gesturechange', noGesture);
      document.removeEventListener('gestureend', noGesture);
    };
  }, []);

  // registra il service worker delle push e azzera il badge quando si apre l'app
  useEffect(() => {
    import('./push').then(({ registraSW, clearBadge }) => { registraSW(); clearBadge(); }).catch(() => {});
  }, []);

  // allinea il colore della barra di stato del browser allo sfondo del tema
  useEffect(() => {
    if (!appRef.current) return;
    const bg = getComputedStyle(appRef.current).getPropertyValue('--bg').trim();
    let meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) { meta = document.createElement('meta'); meta.name = 'theme-color'; document.head.appendChild(meta); }
    if (bg) meta.setAttribute('content', bg);
  }, [theme, mode, user]);

  return (
    <div className="m-app" data-theme={theme} data-mode={mode} ref={appRef}>
      {!user ? (
        <MobileLogin />
      ) : (
        <MobileTheme.Provider value={{ theme, mode }}>
          <SingleNotifProvider>
          <VerificaProvider>
          <BrowserRouter basename="/m">
            <Routes>
              <Route element={<MobileLayout theme={theme} mode={mode} setTheme={setTheme} setMode={setMode} onOpenInstall={() => setInstallOpen(true)} />}>
                <Route index element={<StatoPage />} />
                <Route path="verifiche" element={can('verifiche') ? <A456Page /> : <Navigate to="/" replace />} />
                <Route path="copertura" element={can('agente_copertura') ? <CoperturaPage /> : <Navigate to="/" replace />} />
                <Route path="vf" element={can('gea') ? <VfPage /> : <Navigate to="/" replace />} />
                <Route path="analisi" element={user?.ruolo === 'admin' ? <AnalisiPage /> : <Navigate to="/" replace />} />
                <Route path="utilizzo" element={user?.ruolo === 'admin' ? <UtilizzoPage /> : <Navigate to="/" replace />} />
                <Route path="regioni" element={can('analisi') ? <RegioniPage /> : <Navigate to="/" replace />} />
                <Route path="cerca" element={can('ricerca_online') ? <CercaPage /> : <Navigate to="/" replace />} />
                <Route path="liste" element={can('numeri') ? <ListePage /> : <Navigate to="/" replace />} />
                <Route path="ssmate" element={can('ssmate') ? <SsmatePage /> : <Navigate to="/" replace />} />
                <Route path="copertura-ssmate" element={can('ssmate_copertura') ? <CoperturaSsmatePage /> : <Navigate to="/" replace />} />
                <Route path="salute" element={can('salute') ? <SalutePage /> : <Navigate to="/" replace />} />
              </Route>
            </Routes>
          </BrowserRouter>
          </VerificaProvider>
          </SingleNotifProvider>
        </MobileTheme.Provider>
      )}
      {/* Banner install: fuori dal ramo loggato, così compare anche sul login (arrivo da QR) */}
      <InstallHint
        open={installOpen}
        onClose={() => setInstallOpen(false)}
        onDontShow={() => { try { localStorage.setItem('m_install_dismissed', '1'); } catch (e) {} setInstallOpen(false); }}
        deferredPrompt={deferredPrompt.current}
        onPromptUsed={() => { deferredPrompt.current = null; }}
      />
    </div>
  );
}
